import React, { useState } from 'react';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaInstagram, FaPaperPlane, FaCheckCircle } from 'react-icons/fa';

const ContactUs: React.FC = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [formKey, setFormKey] = useState(0);
    const [submissionMessage, setSubmissionMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await fetch('/api/sendContactEmail', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, phone, subject, message }),
            });

            if (response.ok) {
                setName('');
                setEmail('');
                setPhone('');
                setSubject('');
                setMessage('');
                setFormKey(prevKey => prevKey + 1);
                setSubmissionMessage('Thank you! Your message has been sent successfully. We\'ll get back to you soon.');
            } else {
                setSubmissionMessage('Sorry, there was an error sending your message. Please try again.');
            }
        } catch (error) {
            setSubmissionMessage('Sorry, there was an error sending your message. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <section className="bg-gradient-to-r from-primary to-primary-dark text-white py-16">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-5xl font-bold mb-4">Contact Us</h1>
                    <p className="text-xl opacity-90 max-w-2xl mx-auto">
                        Get in touch with us for bookings, inquiries, or any assistance you need
                    </p>
                </div>
            </section>

            <div className="container mx-auto px-4 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Contact Information */}
                    <div className="space-y-8">
                        <div>
                            <h2 className="text-3xl font-bold text-text mb-6">Get In Touch</h2>
                            <p className="text-gray-600 text-lg leading-relaxed mb-8">
                                We're here to help make your stay at Sunrise Agri Resorts unforgettable. 
                                Reach out to us for reservations, special requests, or any questions you may have.
                            </p>
                        </div>

                        {/* Contact Details */}
                        <div className="space-y-6">
                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <FaMapMarkerAlt className="text-primary text-xl" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-text mb-1">Address</h3>
                                    <p className="text-gray-600">
                                        Bharthapuram (vill)<br />
                                        bowdara(po), s.kota<br />
                                        Andhra Pradesh 535145<br />
                                        India
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <FaPhone className="text-primary text-xl" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-text mb-1">Phone</h3>
                                    <p className="text-gray-600">+91 9121561555</p>
                                    <p className="text-sm text-gray-500">Available 24/7 for bookings</p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <FaEnvelope className="text-primary text-xl" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-text mb-1">Email</h3>
                                    <p className="text-gray-600">sunriseagriresorts@yahoo.com</p>
                                    <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <FaClock className="text-primary text-xl" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-text mb-1">Check-in Hours</h3>
                                    <p className="text-gray-600">
                                        Check-in: 12:00 PM onwards<br />
                                        Check-out: 11:00 AM
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <FaInstagram className="text-primary text-xl" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-text mb-1">Follow Us</h3>
                                    <a 
                                        href="https://www.instagram.com/sunrise_resorts_skota/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:text-primary-dark transition-colors duration-200"
                                    >
                                        @sunrise_resorts_s.kota
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl p-6">
                            <h3 className="font-semibold text-text mb-4">Quick Actions</h3>
                            <div className="space-y-3">
                                <a 
                                    href="/book-room" 
                                    className="block bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 text-center"
                                >
                                    Book Your Stay
                                </a>
                                <a 
                                    href="/gallery" 
                                    className="block bg-white hover:bg-gray-50 text-primary border-2 border-primary px-6 py-3 rounded-lg font-semibold transition-colors duration-200 text-center"
                                >
                                    View Gallery
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="bg-white rounded-2xl shadow-large p-8">
                        <h2 className="text-2xl font-bold text-text mb-6">Send Us a Message</h2>
                        
                        {submissionMessage && (
                            <div className={`mb-6 p-4 rounded-lg flex items-center ${
                                submissionMessage.includes('error') || submissionMessage.includes('Sorry')
                                    ? 'bg-red-50 border border-red-200 text-red-800'
                                    : 'bg-green-50 border border-green-200 text-green-800'
                            }`}>
                                <FaCheckCircle className="mr-3 flex-shrink-0" />
                                <p>{submissionMessage}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} key={formKey} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        placeholder="Your full name"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="+91 9121561555"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Email Address *
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    placeholder="your.email@example.com"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                                />
                            </div>

                            <div>
                                <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Subject *
                                </label>
                                <select
                                    id="subject"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                                >
                                    <option value="">Select a subject</option>
                                    <option value="Booking Inquiry">Booking Inquiry</option>
                                    <option value="Room Information">Room Information</option>
                                    <option value="Special Requests">Special Requests</option>
                                    <option value="Feedback">Feedback</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Message *
                                </label>
                                <textarea
                                    id="message"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    required
                                    rows={5}
                                    placeholder="Tell us how we can help you..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 resize-none"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className={`w-full py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center ${
                                    isLoading
                                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                        : 'bg-primary hover:bg-primary-dark text-white shadow-medium hover:shadow-large transform hover:-translate-y-1'
                                }`}
                            >
                                {isLoading ? (
                                    <span className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sending...
                                    </span>
                                ) : (
                                    <span className="flex items-center">
                                        <FaPaperPlane className="mr-2" />
                                        Send Message
                                    </span>
                                )}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Map Section */}
                <div className="mt-16">
                    <div className="bg-white rounded-2xl shadow-large overflow-hidden">
                        <div className="p-8 text-center border-b border-gray-200">
                            <h2 className="text-3xl font-bold text-text mb-4">Find Us on the Map</h2>
                            <p className="text-gray-600">Located in the beautiful countryside of Andhra Pradesh</p>
                        </div>
                        <div className="aspect-[16/9] w-full">
                            <iframe
                                title="Sunrise Agri Resorts Location"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3791.044778062368!2d83.13147797557933!3d18.1618510800327!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a3bc5c03980bac1%3A0x92be252eef01bbf9!2sSunrise%20Agri%20Resorts!5e0!3m2!1sen!2sus!4v1766862077672!5m2!1sen!2sus"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen={false}
                                loading="lazy"
                                className="w-full h-full"
                            ></iframe>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactUs;